import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney, formatDate } from "@/lib/api";
import { Kicker, LoadingCard, ErrorCard, EmptyState } from "@/components/common";
import { Check, X, Eye } from "lucide-react";

export default function Approvals() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [acting, setActing] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/expenses", { params: { status_filter: "pending" } });
      setRows(res.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load approvals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const act = async (id, status) => {
    setActing(id + status);
    try {
      await api.patch(`/expenses/${id}/status`, { status });
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      setError(e?.response?.data?.detail || "Action failed");
    } finally {
      setActing(null);
    }
  };

  if (loading)
    return (
      <div className="max-w-6xl mx-auto px-6 py-8">
        <LoadingCard />
      </div>
    );
  if (error)
    return (
      <div className="max-w-6xl mx-auto px-6 py-8">
        <ErrorCard message={error} onRetry={load} />
      </div>
    );

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 animate-fade-up">
      <div className="mb-6">
        <Kicker>Approvals</Kicker>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-zinc-950 mt-1">Pending approvals</h1>
        <p className="mt-2 text-sm text-zinc-500">
          {rows.length} {rows.length === 1 ? "expense" : "expenses"} waiting for your review.
        </p>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title="You're all caught up"
          description="No pending expenses to review right now."
        />
      ) : (
        <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-zinc-500 text-xs uppercase tracking-wider">
                <th className="px-5 py-3 font-medium">Date</th>
                <th className="px-5 py-3 font-medium">Employee</th>
                <th className="px-5 py-3 font-medium">Merchant</th>
                <th className="px-5 py-3 font-medium">Category</th>
                <th className="px-5 py-3 font-medium">Customer</th>
                <th className="px-5 py-3 font-medium text-right">Amount</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} data-testid={`approval-row-${e.id}`} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="px-5 py-4 text-zinc-700 whitespace-nowrap">{formatDate(e.date)}</td>
                  <td className="px-5 py-4 text-zinc-700">{e.user_name}</td>
                  <td className="px-5 py-4 text-zinc-900 font-medium">{e.merchant || "—"}</td>
                  <td className="px-5 py-4 text-zinc-700">{e.category}</td>
                  <td className="px-5 py-4 text-zinc-700">{e.customer || "—"}</td>
                  <td className="px-5 py-4 text-right text-zinc-900 font-medium tabular-nums">
                    {formatMoney(e.amount, e.currency)}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/expense/${e.id}`}
                        data-testid={`approval-view-${e.id}`}
                        className="inline-flex items-center gap-1 rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50"
                      >
                        <Eye className="w-3.5 h-3.5" /> View
                      </Link>
                      <button
                        data-testid={`approval-reject-${e.id}`}
                        disabled={acting === e.id + "rejected"}
                        onClick={() => act(e.id, "rejected")}
                        className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-white px-2.5 py-1.5 text-xs text-red-700 hover:bg-red-50 disabled:opacity-60"
                      >
                        <X className="w-3.5 h-3.5" /> Reject
                      </button>
                      <button
                        data-testid={`approval-approve-${e.id}`}
                        disabled={acting === e.id + "approved"}
                        onClick={() => act(e.id, "approved")}
                        className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 text-white px-2.5 py-1.5 text-xs font-medium hover:bg-emerald-700 disabled:opacity-60"
                      >
                        <Check className="w-3.5 h-3.5" /> Approve
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
